import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaShieldAlt, FaArrowLeft, FaEdit, FaPhone, FaFax, FaEnvelope,
  FaMapMarkerAlt, FaSave, FaTimes, FaUser, FaGlobe, FaBriefcase,
  FaPlus, FaTrash, FaUsers, FaDollarSign, FaTruck, FaExclamationTriangle
} from 'react-icons/fa';
import './SheriffOfficeDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SheriffOfficeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [office, setOffice] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedOffice, setEditedOffice] = useState(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    name: '',
    title: '',
    phone: '',
    phone_ext: '',
    email: '',
    notes: ''
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchOfficeDetails();
  }, [id]);

  const fetchOfficeDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/sheriff-offices/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setOffice(data.data);
        setEditedOffice(data.data);
        setStaff(data.data.staff || []);
        setError('');
      } else {
        setError('Sheriff office not found');
      }
    } catch (err) {
      console.error('Error fetching office details:', err);
      setError('Failed to load office details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOfficeEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/sheriff-offices/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editedOffice)
      });

      const data = await response.json();

      if (data.success) {
        setOffice(data.data);
        setEditedOffice(data.data);
        setEditMode(false);
      } else {
        alert('Failed to update office');
      }
    } catch (err) {
      console.error('Error updating office:', err);
      alert('Failed to update office');
    }
  };

  const handleCancelEdit = () => {
    setEditedOffice(office);
    setEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setEditedOffice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.title) {
      alert('Name and title are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/sheriff-offices/${id}/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newStaff)
      });

      const data = await response.json();

      if (data.success) {
        await fetchOfficeDetails();
        setNewStaff({
          name: '',
          title: '',
          phone: '',
          phone_ext: '',
          email: '',
          notes: ''
        });
        setShowAddStaffModal(false);
      } else {
        alert('Failed to add staff member');
      }
    } catch (err) {
      console.error('Error adding staff:', err);
      alert('Failed to add staff member');
    }
  };

  const handleStaffInputChange = (field, value) => {
    setNewStaff(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="office-detail-container">
        <div className="loading">Loading office details...</div>
      </div>
    );
  }

  if (error || !office) {
    return (
      <div className="office-detail-container">
        <div className="error-message">{error || 'Office not found'}</div>
        <button className="btn-back" onClick={() => navigate('/address-book/sheriff-offices')}>
          <FaArrowLeft /> Back to Sheriff's Offices
        </button>
      </div>
    );
  }

  const displayData = editMode ? editedOffice : office;

  // Build full address string
  const fullAddress = [
    displayData.address_street,
    displayData.address_city || displayData.city,
    displayData.address_state,
    displayData.address_zip
  ].filter(Boolean).join(', ');

  // Build staff display with breakdown
  const staffDisplay = displayData.total_staff
    ? `${displayData.total_staff} total${displayData.sworn_staff || displayData.civilian_staff
        ? ` (${[
            displayData.sworn_staff ? `${displayData.sworn_staff} sworn` : '',
            displayData.civilian_staff ? `${displayData.civilian_staff} civilian` : ''
          ].filter(Boolean).join(', ')})`
        : ''}`
    : null;

  // Extract leadership from staff (Sheriff and Undersheriff)
  const sheriff = staff.find(s => s.title?.toLowerCase().includes('sheriff') && !s.title?.toLowerCase().includes('under'));
  const undersheriff = staff.find(s => s.title?.toLowerCase().includes('undersheriff'));
  const otherStaff = staff.filter(s =>
    s.id !== sheriff?.id && s.id !== undersheriff?.id
  );

  return (
    <div className="office-detail-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/address-book/sheriff-offices')}>
            <FaArrowLeft /> Back
          </button>
        </div>

        <div className="header-center">
          <h1>{office.name}</h1>
        </div>

        <div className="header-right">
          {user.role === 'admin' && !editMode && (
            <button className="btn-edit-office" onClick={() => setEditMode(true)}>
              <FaEdit /> Edit
            </button>
          )}
          {editMode && (
            <>
              <button className="btn-save" onClick={handleSaveOfficeEdit}>
                <FaSave /> Save
              </button>
              <button className="btn-cancel" onClick={handleCancelEdit}>
                <FaTimes /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Leadership Section */}
      {(sheriff || undersheriff) && (
        <div className="leadership-section">
          {sheriff && (
            <div className="leadership-card">
              <div className="leadership-photo-container">
                <div className="leadership-photo-placeholder">
                  <FaUser size={48} />
                  <span>Photo</span>
                </div>
                {user.role === 'admin' && (
                  <button className="btn-upload-photo" title="Upload Photo">
                    <FaPlus size={12} />
                  </button>
                )}
              </div>
              <div className="leadership-info">
                <div className="leadership-title">{sheriff.title}</div>
                <div className="leadership-name">{sheriff.name}</div>
                {sheriff.phone && (
                  <div className="leadership-contact">
                    <FaPhone />
                    <span>{sheriff.phone}{sheriff.phone_ext && ` ext. ${sheriff.phone_ext}`}</span>
                  </div>
                )}
                {sheriff.email && (
                  <div className="leadership-contact">
                    <FaEnvelope />
                    <a href={`mailto:${sheriff.email}`}>{sheriff.email}</a>
                  </div>
                )}
              </div>
            </div>
          )}

          {undersheriff && (
            <div className="leadership-card">
              <div className="leadership-photo-container">
                <div className="leadership-photo-placeholder">
                  <FaUser size={48} />
                  <span>Photo</span>
                </div>
                {user.role === 'admin' && (
                  <button className="btn-upload-photo" title="Upload Photo">
                    <FaPlus size={12} />
                  </button>
                )}
              </div>
              <div className="leadership-info">
                <div className="leadership-title">{undersheriff.title}</div>
                <div className="leadership-name">{undersheriff.name}</div>
                {undersheriff.phone && (
                  <div className="leadership-contact">
                    <FaPhone />
                    <span>{undersheriff.phone}{undersheriff.phone_ext && ` ext. ${undersheriff.phone_ext}`}</span>
                  </div>
                )}
                {undersheriff.email && (
                  <div className="leadership-contact">
                    <FaEnvelope />
                    <a href={`mailto:${undersheriff.email}`}>{undersheriff.email}</a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content - Single Grid Layout */}
      <div className="detail-content-unified">
        <div className="unified-grid">

          {/* BASIC INFORMATION */}
          <div className="grid-section-header">Basic Information</div>

          <div className="grid-item">
            <label>County</label>
            {editMode ? (
              <input
                type="text"
                value={displayData.county || ''}
                onChange={(e) => handleInputChange('county', e.target.value)}
                placeholder="County"
              />
            ) : (
              <span>{displayData.county || 'N/A'}</span>
            )}
          </div>

          {!editMode && fullAddress && (
            <div className="grid-item full-width">
              <label><FaMapMarkerAlt /> Address</label>
              <span>{fullAddress}</span>
            </div>
          )}

          {editMode && (
            <>
              <div className="grid-item full-width">
                <label><FaMapMarkerAlt /> Street Address</label>
                <input
                  type="text"
                  value={displayData.address_street || ''}
                  onChange={(e) => handleInputChange('address_street', e.target.value)}
                  placeholder="Street address"
                />
              </div>

              <div className="grid-item">
                <label>City</label>
                <input
                  type="text"
                  value={displayData.address_city || displayData.city || ''}
                  onChange={(e) => handleInputChange('address_city', e.target.value)}
                  placeholder="City"
                />
              </div>

              <div className="grid-item">
                <label>State</label>
                <input
                  type="text"
                  value={displayData.address_state || ''}
                  onChange={(e) => handleInputChange('address_state', e.target.value)}
                  placeholder="State"
                />
              </div>

              <div className="grid-item">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={displayData.address_zip || ''}
                  onChange={(e) => handleInputChange('address_zip', e.target.value)}
                  placeholder="ZIP code"
                />
              </div>
            </>
          )}

          {/* CONTACT NUMBERS */}
          <div className="grid-section-header">Contact Numbers</div>

          {(displayData.phone || editMode) && (
            <div className="grid-item">
              <label><FaPhone /> Main Phone</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Main phone"
                />
              ) : (
                <span>{displayData.phone || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.dispatch_number || editMode) && (
            <div className="grid-item">
              <label><FaPhone /> Dispatch</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.dispatch_number || ''}
                  onChange={(e) => handleInputChange('dispatch_number', e.target.value)}
                  placeholder="Dispatch number"
                />
              ) : (
                <span>{displayData.dispatch_number || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.emergency_number || editMode) && (
            <div className="grid-item">
              <label><FaExclamationTriangle /> Emergency</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.emergency_number || ''}
                  onChange={(e) => handleInputChange('emergency_number', e.target.value)}
                  placeholder="Emergency number"
                />
              ) : (
                <span>{displayData.emergency_number || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.non_emergency_number || editMode) && (
            <div className="grid-item">
              <label><FaPhone /> Non-Emergency</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.non_emergency_number || ''}
                  onChange={(e) => handleInputChange('non_emergency_number', e.target.value)}
                  placeholder="Non-emergency number"
                />
              ) : (
                <span>{displayData.non_emergency_number || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.fax || editMode) && (
            <div className="grid-item">
              <label><FaFax /> Fax</label>
              {editMode ? (
                <input
                  type="tel"
                  value={displayData.fax || ''}
                  onChange={(e) => handleInputChange('fax', e.target.value)}
                  placeholder="Fax number"
                />
              ) : (
                <span>{displayData.fax || 'N/A'}</span>
              )}
            </div>
          )}

          {/* DIGITAL RESOURCES */}
          <div className="grid-section-header">Digital Resources</div>

          {(displayData.website || editMode) && (
            <div className="grid-item full-width">
              <label><FaGlobe /> Website</label>
              {editMode ? (
                <input
                  type="url"
                  value={displayData.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="Website URL"
                />
              ) : (
                <span>
                  {displayData.website ? (
                    <a href={displayData.website} target="_blank" rel="noopener noreferrer">
                      {displayData.website}
                    </a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {(displayData.email || editMode) && (
            <div className="grid-item full-width">
              <label><FaEnvelope /> Email</label>
              {editMode ? (
                <input
                  type="email"
                  value={displayData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Email address"
                />
              ) : (
                <span>
                  {displayData.email ? (
                    <a href={`mailto:${displayData.email}`}>{displayData.email}</a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {(displayData.recruitment_website || editMode) && (
            <div className="grid-item full-width">
              <label><FaGlobe /> Recruitment</label>
              {editMode ? (
                <input
                  type="url"
                  value={displayData.recruitment_website || ''}
                  onChange={(e) => handleInputChange('recruitment_website', e.target.value)}
                  placeholder="Recruitment website URL"
                />
              ) : (
                <span>
                  {displayData.recruitment_website ? (
                    <a href={displayData.recruitment_website} target="_blank" rel="noopener noreferrer">
                      {displayData.recruitment_website}
                    </a>
                  ) : 'N/A'}
                </span>
              )}
            </div>
          )}

          {/* STAFFING & BUDGET */}
          <div className="grid-section-header">Staffing & Budget</div>

          {!editMode && staffDisplay && (
            <div className="grid-item full-width">
              <label><FaUsers /> Total Staff</label>
              <span>{staffDisplay}</span>
            </div>
          )}

          {editMode && (
            <>
              <div className="grid-item">
                <label><FaUsers /> Total Staff</label>
                <input
                  type="number"
                  value={displayData.total_staff || ''}
                  onChange={(e) => handleInputChange('total_staff', e.target.value)}
                  placeholder="Total staff"
                />
              </div>

              <div className="grid-item">
                <label><FaUsers /> Sworn Staff</label>
                <input
                  type="number"
                  value={displayData.sworn_staff || ''}
                  onChange={(e) => handleInputChange('sworn_staff', e.target.value)}
                  placeholder="Sworn staff"
                />
              </div>

              <div className="grid-item">
                <label><FaUsers /> Civilian Staff</label>
                <input
                  type="number"
                  value={displayData.civilian_staff || ''}
                  onChange={(e) => handleInputChange('civilian_staff', e.target.value)}
                  placeholder="Civilian staff"
                />
              </div>
            </>
          )}

          {(displayData.annual_budget || editMode) && (
            <div className="grid-item">
              <label><FaDollarSign /> Annual Budget</label>
              {editMode ? (
                <input
                  type="text"
                  value={displayData.annual_budget || ''}
                  onChange={(e) => handleInputChange('annual_budget', e.target.value)}
                  placeholder="Annual budget"
                />
              ) : (
                <span>{displayData.annual_budget || 'N/A'}</span>
              )}
            </div>
          )}

          {(displayData.annual_transports || editMode) && (
            <div className="grid-item">
              <label><FaTruck /> Annual Transports</label>
              {editMode ? (
                <input
                  type="number"
                  value={displayData.annual_transports || ''}
                  onChange={(e) => handleInputChange('annual_transports', e.target.value)}
                  placeholder="Annual transports"
                />
              ) : (
                <span>{displayData.annual_transports || 'N/A'}</span>
              )}
            </div>
          )}
        </div>

        {/* STAFF CONTACTS SECTION */}
        <div className="staff-contacts-section">
          <div className="staff-contacts-header">
            <h2>Staff Contacts</h2>
            {user.role === 'admin' && (
              <button className="btn-add-staff" onClick={() => setShowAddStaffModal(true)}>
                <FaPlus /> Add Staff
              </button>
            )}
          </div>

          {otherStaff.length === 0 ? (
            <div className="no-staff-placeholder">
              <FaUser size={32} />
              <p>No additional staff contacts</p>
            </div>
          ) : (
            <div className="staff-list-compact">
              {otherStaff.map((member) => (
                <div key={member.id} className="staff-card-compact">
                  <div className="staff-card-header">
                    <div className="staff-name">{member.name}</div>
                    {member.title && (
                      <span className="staff-title-badge">
                        {member.title}
                      </span>
                    )}
                  </div>
                  <div className="staff-card-details">
                    {member.phone && (
                      <div className="staff-detail-item">
                        <FaPhone size={12} />
                        <span>
                          {member.phone}
                          {member.phone_ext && ` ext. ${member.phone_ext}`}
                        </span>
                      </div>
                    )}
                    {member.email && (
                      <div className="staff-detail-item">
                        <FaEnvelope size={12} />
                        <a href={`mailto:${member.email}`}>{member.email}</a>
                      </div>
                    )}
                    {member.notes && (
                      <div className="staff-notes">{member.notes}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="modal-overlay" onClick={() => setShowAddStaffModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Staff Member</h2>
              <button className="modal-close" onClick={() => setShowAddStaffModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newStaff.name}
                  onChange={(e) => handleStaffInputChange('name', e.target.value)}
                  placeholder="Enter staff member name"
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={newStaff.title}
                  onChange={(e) => handleStaffInputChange('title', e.target.value)}
                  placeholder="e.g., Chief Deputy, Captain, Detective"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newStaff.phone}
                    onChange={(e) => handleStaffInputChange('phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>

                <div className="form-group">
                  <label>Extension</label>
                  <input
                    type="text"
                    value={newStaff.phone_ext}
                    onChange={(e) => handleStaffInputChange('phone_ext', e.target.value)}
                    placeholder="Ext."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => handleStaffInputChange('email', e.target.value)}
                  placeholder="Email address"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newStaff.notes}
                  onChange={(e) => handleStaffInputChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddStaffModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleAddStaff}>
                <FaPlus /> Add Staff Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SheriffOfficeDetail;
